import { DateTime } from "luxon";
import z from "zod";

export enum CriteriaType {
  VARIANT_REACHED_MIN_ROI = "VARIANT_REACHED_MIN_ROI",
  FIRST_TO_MIN_ROI = "FIRST_TO_MIN_ROI",
  HIGHEST_ROI_AT_TIME = "HIGHEST_ROI_AT_TIME",
}

export enum AutosendOptionType {
  SEND_TO_FULL = "SEND_TO_FULL",
  SEND_TO_PORTION = "SEND_TO_PORTION",
}

// Required Date field that distinguishes between empty input (returns "Required")
// and unparseable input (falls back to zod's default error). errorMap alone can't
// make this distinction because zod's coerce runs before the errorMap sees the
// input — by then null / "" / undefined have all been coerced to an Invalid Date.
export const dateForForm = z
  .union([z.string(), z.number(), z.date(), z.null(), z.undefined()])
  .superRefine((v, ctx) => {
    if (v == null || v === "") {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Required" });
    }
  })
  .pipe(
    z.coerce.date({
      errorMap: (_issue, ctx) => ({ message: ctx.defaultError }),
    })
  );

// What to do automatically after the evaluation succeeds and a winner is picked.
const AutosendOptionSchema = z
  .discriminatedUnion("type", [
    z.object({ type: z.literal(AutosendOptionType.SEND_TO_FULL) }),
    z.object({
      type: z.literal(AutosendOptionType.SEND_TO_PORTION),
      textsToSend: z.coerce.number().int().min(1),
    }),
  ])
  .nullable()
  .default(null);

export type AutosendOption = z.infer<typeof AutosendOptionSchema>;

const BasePartialSendInputSchema = z.object({
  variantIds: z.array(z.string().uuid()).min(1),

  // Number of minutes to wait after sending to begin evaluating.
  evaluationDelayInMin: z.coerce.number().int().optional().default(0),

  // Exact time at which the evaluation window expires.
  expiresAt: z.coerce.date(),

  // Intended for evaluations that have an evaluationDelayInMin and are required to run only once.
  runOnce: z.boolean().optional().default(false),

  autosendOption: AutosendOptionSchema,
});

const BaseRoiDonationInputSchema = BasePartialSendInputSchema.extend({
  expiresAt: dateForForm,
  minRoi: z.coerce
    .number()
    .nullish()
    .refine(v => v == null || v >= 0, { message: "Must be a positive value" })
    .transform(v => v ?? 0),
  minNumberOfDonations: z.coerce
    .number()
    .int()
    .nullish()
    .refine(v => v == null || v >= 0, { message: "Must be a positive value" })
    .transform(v => v ?? 0),
  excludeDonationsOverInCents: z.coerce
    .number()
    .int()
    .optional()
    .refine(v => v == null || v >= 0, { message: "Must be a positive value" }),
});

export const FirstToMinRoiCriteriaSchema = BaseRoiDonationInputSchema.extend({
  type: z.literal(CriteriaType.FIRST_TO_MIN_ROI),
});

export const HighestRoiAtTimeCriteriaBaseSchema =
  BaseRoiDonationInputSchema.extend({
    type: z.literal(CriteriaType.HIGHEST_ROI_AT_TIME),
  });

export const VariantReachedMinRoiCriteriaBaseSchema =
  BaseRoiDonationInputSchema.extend({
    type: z.literal(CriteriaType.VARIANT_REACHED_MIN_ROI),
    expiresAt: dateForForm.optional(),
  });

export const EvaluationCriteriaSchema = z
  .discriminatedUnion("type", [
    VariantReachedMinRoiCriteriaBaseSchema,
    FirstToMinRoiCriteriaSchema,
    HighestRoiAtTimeCriteriaBaseSchema,
  ])
  .transform(data => {
    if (data.type === CriteriaType.HIGHEST_ROI_AT_TIME) {
      return { ...data, runOnce: true, evaluationDelayInMin: 0 };
    }
    return data;
  });

export type EvaluationCriteriaInput = z.input<typeof EvaluationCriteriaSchema>;

export type EvaluationCriteriaOutput = z.infer<typeof EvaluationCriteriaSchema>;

// Validates the {expiresAt, evaluation} payload — used by the partial-send
// form on the frontend and the schedule/start resolvers on the backend. All
// cross-field rules live in a single consolidated superRefine so they each
// run in one pass and surface independently, rather than being short-circuited
// by an unrelated inner-schema abort.
export const PartialSendEvaluationInputSchema = z
  .object({
    expiresAt: dateForForm,
    evaluation: EvaluationCriteriaSchema,
  })
  .superRefine((data, ctx) => {
    const { evaluation, expiresAt: dontSendAfter } = data;
    const stopCheckingForWinner = evaluation?.expiresAt;

    // Mirror first (VARIANT only). Must happen before per-type dispatch so
    // evaluation.expiresAt is set when the cases below run.
    if (
      evaluation.type === CriteriaType.VARIANT_REACHED_MIN_ROI &&
      dontSendAfter
    ) {
      evaluation.expiresAt = dontSendAfter;
    }

    // Universal: both deadlines (when set) must be in the future.
    if (dontSendAfter <= new Date()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Must be in the future",
        path: ["expiresAt"],
      });
    }

    if (stopCheckingForWinner && stopCheckingForWinner <= new Date()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Must be in the future",
        path: ["evaluation", "expiresAt"],
      });
    }

    switch (evaluation.type) {
      case CriteriaType.FIRST_TO_MIN_ROI: {
        if (!(evaluation.minRoi > 0)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Must be at least 1",
            path: ["evaluation", "minRoi"],
          });
        }

        const delayUntil = DateTime.now()
          .plus({ minutes: evaluation.evaluationDelayInMin })
          .toJSDate();

        if (stopCheckingForWinner && delayUntil >= stopCheckingForWinner) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Must be after start checking for a winner",
            path: ["evaluation", "expiresAt"],
          });
        }

        if (stopCheckingForWinner && stopCheckingForWinner >= dontSendAfter) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Must be after stop checking for a winner",
            path: ["expiresAt"],
          });
        }
        break;
      }
      case CriteriaType.HIGHEST_ROI_AT_TIME: {
        if (stopCheckingForWinner && stopCheckingForWinner >= dontSendAfter) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Must be after Date/Time to Pick a Winner",
            path: ["expiresAt"],
          });
        }

        break;
      }
      case CriteriaType.VARIANT_REACHED_MIN_ROI: {
        if (data.evaluation.minRoi || data.evaluation.minNumberOfDonations) {
          return;
        }
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Either a minimum ROI or minimum number of donations must be specified",
          path: ["evaluation", "minRoi"],
        });
        break;
      }
    }
  });

const RoiSingleVariantResultSchema = z.object({
  variantId: z.string().uuid(),
  roi: z.number(),
  donationCount: z.number(),
  reachedMinRoi: z.boolean(),
  excludedDonations: z.array(z.number().int()).default([]),
  totalRaisedInCents: z.number().int().optional().default(0),
  totalSent: z.number().int().optional().default(0),
  variantOrder: z.number().int().nullable().optional().default(null),
  clickCount: z.number().int().optional().default(0),
  replyCount: z.number().int().optional().default(0),
  filterCount: z.number().int().optional().default(0),
  optOutCount: z.number().int().optional().default(0),
});

type RoiSingleVariantResult = z.infer<typeof RoiSingleVariantResultSchema>;

const RoiMultiVariantResultSchema = z.object({
  winningVariantId: z.string().uuid().optional(),
  variantResults: z.array(RoiSingleVariantResultSchema),
});

export const EvaluationResultSchema = z.discriminatedUnion("type", [
  z
    .object({ type: z.literal(CriteriaType.VARIANT_REACHED_MIN_ROI) })
    .merge(RoiSingleVariantResultSchema),
  z
    .object({ type: z.literal(CriteriaType.FIRST_TO_MIN_ROI) })
    .merge(RoiMultiVariantResultSchema),
  z
    .object({ type: z.literal(CriteriaType.HIGHEST_ROI_AT_TIME) })
    .merge(RoiMultiVariantResultSchema),
]);

export type EvaluationResult = z.infer<typeof EvaluationResultSchema>;

function buildExcludedDonationsMap(
  variantResults: RoiSingleVariantResult[]
): Record<string, number> {
  const map: Record<string, number> = {};
  for (const vr of variantResults) {
    map[vr.variantId] = vr.excludedDonations.length;
  }
  return map;
}

const EvaluationSuccessEventSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal(CriteriaType.VARIANT_REACHED_MIN_ROI),
    partialSendEvaluationId: z.string().uuid(),
    variantId: z.string().uuid(),
    roi: z.number(),
    excludedDonationsByScriptVariantId: z.record(z.string(), z.number()),
  }),
  z.object({
    type: z.literal(CriteriaType.FIRST_TO_MIN_ROI),
    partialSendEvaluationId: z.string().uuid(),
    bestScriptVariantId: z.string().uuid().optional(),
    bestScriptVariantRoi: z.number(),
    excludedDonationsByScriptVariantId: z.record(z.string(), z.number()),
  }),
  z.object({
    type: z.literal(CriteriaType.HIGHEST_ROI_AT_TIME),
    partialSendEvaluationId: z.string().uuid(),
    bestScriptVariantId: z.string().uuid().optional(),
    bestScriptVariantRoi: z.number(),
    excludedDonationsByScriptVariantId: z.record(z.string(), z.number()),
  }),
]);

export type EvaluationSuccessEvent = z.infer<
  typeof EvaluationSuccessEventSchema
>;

export function buildSuccessEvent(
  result: EvaluationResult,
  partialSendEvaluationId: string
): EvaluationSuccessEvent {
  if (result.type === CriteriaType.VARIANT_REACHED_MIN_ROI) {
    return EvaluationSuccessEventSchema.parse({
      type: result.type,
      partialSendEvaluationId,
      variantId: result.variantId,
      roi: result.roi,
      excludedDonationsByScriptVariantId: {
        [result.variantId]: result.excludedDonations.length,
      },
    });
  }

  return EvaluationSuccessEventSchema.parse({
    type: result.type,
    partialSendEvaluationId,
    bestScriptVariantId: result.winningVariantId,
    bestScriptVariantRoi: result.variantResults.reduce(
      (max, vr) => Math.max(max, vr.roi),
      0
    ),
    excludedDonationsByScriptVariantId: buildExcludedDonationsMap(
      result.variantResults
    ),
  });
}
